import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import App from './App';
import './index.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthProvider';
import { DepartmentProvider } from './contexts/DepartmentProvider';
import { queryClient } from './lib/queryClient';
import { ensureFreshDeploy } from './utils/deployVersionCheck';

const extensionNoisePatterns = [/triggerAutofillScriptInjection/i, /background\.js/i, /chrome-extension:\/\//i];

const isExtensionNoise = (message?: string, source?: string) => {
  if (!message && !source) return false;
  return extensionNoisePatterns.some((pattern) => pattern.test(message ?? '') || pattern.test(source ?? ''));
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (isExtensionNoise(event.message, event.filename)) {
      event.preventDefault();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: string; stack?: string } | string | undefined;
    const message = typeof reason === 'string' ? reason : reason?.message;
    const source = typeof reason === 'string' ? undefined : reason?.stack;
    if (isExtensionNoise(message, source)) {
      event.preventDefault();
    }
  });
}

function mountApp() {
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <React.StrictMode>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <DepartmentProvider>
              <App />
            </DepartmentProvider>
          </AuthProvider>
          {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
        </QueryClientProvider>
      </Router>
    </React.StrictMode>,
  );
}

void ensureFreshDeploy().then(mountApp);
